import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { ChevronLeft, Package, CheckCircle, Loader2, Minus, Plus, Phone, ShieldCheck, Truck, PhoneCall, HandCoins, MousePointerClick, Eye, CalendarClock } from "lucide-react";

const NUMERO_TOPCOCOON = "+32 470 37 57 35";

export default function PreCommandePublic() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("list"); // list | form | success
  const [selected, setSelected] = useState(null);
  const [quantite, setQuantite] = useState(1);
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    adresse: "",
    code_postal: "",
    ville: "",
    numero_commande: "",
    commentaire: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/products`);
        setProducts(res.data || []);
      } catch { setError("Impossible de charger les produits"); }
      finally { setLoading(false); }
    })();
  }, []);

  const pickProduct = (p) => {
    setSelected(p);
    setQuantite(1);
    setStep("form");
    setError("");
  };

  const submitPrecommande = async () => {
    if (!form.nom.trim()) { setError("Nom requis"); return; }
    if (!form.telephone.trim() && !form.email.trim()) { setError("Téléphone ou email requis"); return; }
    if (!form.adresse.trim() || !form.code_postal.trim() || !form.ville.trim()) {
      setError("Merci de renseigner votre adresse complète (rue, code postal, ville)");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/public/precommande`, {
        sku: selected.sku,
        quantite,
        nom: form.nom.trim(),
        telephone: form.telephone.trim(),
        email: form.email.trim(),
        adresse: form.adresse.trim(),
        code_postal: form.code_postal.trim(),
        ville: form.ville.trim(),
        numero_commande: form.numero_commande.trim(),
        commentaire: form.commentaire.trim(),
      });
      setStep("success");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur — réessaye plus tard");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-3 sm:p-6" data-testid="precommande-public">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-indigo-700">TopCocoon</h1>
          <p className="text-sm text-slate-500">Précommande produit</p>
        </div>

        {/* ---- STEP LIST ---- */}
        {step === "list" && (
          <div className="space-y-3">
            {/* Bloc "Comment fonctionne la précommande" */}
            <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-4 sm:p-5" data-testid="how-it-works">
              <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Comment fonctionne la précommande ?
              </p>
              <ol className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">1</span>
                  <span>Choisissez votre modèle, votre couleur et la quantité souhaitée.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">2</span>
                  <span><strong>Réservez gratuitement</strong>, aucun paiement n&apos;est demandé maintenant.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">3</span>
                  <span>Dès que vos chaises sont disponibles, TopCocoon vous contacte.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">4</span>
                  <span>Nous organisons ensemble la livraison à domicile.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">5</span>
                  <span>Le paiement s&apos;effectue à la livraison et un reçu de paiement est fourni.</span>
                </li>
              </ol>
            </div>

            <p className="text-center text-slate-600 text-sm pt-1">Choisis le produit qui te fait envie 👇</p>

            {/* CTA — venir essayer au showroom avant de commander */}
            <a
              href="/prendre-rdv?type=visite"
              className="block bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-2xl shadow-sm p-4 hover:shadow-md transition"
              data-testid="cta-showroom-visite"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">Envie de venir essayer avant ?</p>
                  <p className="text-xs text-indigo-100 leading-snug">Réservez un créneau au showroom d&apos;Anderlecht</p>
                </div>
                <ChevronLeft className="w-5 h-5 rotate-180" />
              </div>
            </a>

            {products.length === 0 && (
              <div className="bg-white rounded-2xl p-6 text-center shadow border">
                <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Aucun produit disponible pour le moment.</p>
                <p className="text-xs text-slate-400 mt-2">Contacte-nous au <a href={`tel:${NUMERO_TOPCOCOON}`} className="text-indigo-600 font-semibold">{NUMERO_TOPCOCOON}</a></p>
              </div>
            )}
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => pickProduct(p)}
                className="w-full bg-white rounded-2xl shadow border hover:border-indigo-400 hover:shadow-lg transition text-left overflow-hidden flex gap-3 p-3"
                data-testid={`product-${p.sku}`}
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-indigo-100 to-emerald-100 flex items-center justify-center">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.modele} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl sm:text-3xl font-bold text-indigo-400">{(p.modele || "?")[0]}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-base leading-tight">{p.modele} <span className="text-slate-500 font-normal">— {p.couleur}</span></p>
                  {p.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center justify-between mt-2 flex-wrap gap-1">
                    <span className="font-bold text-emerald-700">{p.prix_b2c_ttc} €</span>
                  </div>
                </div>
              </button>
            ))}

            <p className="text-[11px] text-slate-400 text-center italic pt-2 px-2">
              La précommande vous permet de réserver votre modèle. La date de disponibilité vous sera communiquée dès qu&apos;elle sera confirmée.
            </p>
          </div>
        )}

        {/* ---- STEP FORM ---- */}
        {step === "form" && selected && (
          <div className="bg-white rounded-2xl shadow-xl border overflow-hidden">
            <div className="bg-indigo-600 text-white p-3 sm:p-4 flex items-center gap-2">
              <button onClick={() => setStep("list")} className="p-1 hover:bg-white/20 rounded" data-testid="back-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{selected.modele} — {selected.couleur}</p>
                <p className="text-xs text-indigo-100">{selected.prix_b2c_ttc} €</p>
              </div>
            </div>

            <div className="p-4 sm:p-5 space-y-3">
              {/* Quantité */}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Quantité</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantite(Math.max(1, quantite - 1))}
                    className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                    data-testid="qty-minus"
                  ><Minus className="w-4 h-4" /></button>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={quantite}
                    onChange={(e) => setQuantite(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
                    className="w-20 h-10 text-center border rounded-lg font-bold text-lg"
                    data-testid="qty-input"
                  />
                  <button
                    onClick={() => setQuantite(Math.min(99, quantite + 1))}
                    className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                    data-testid="qty-plus"
                  ><Plus className="w-4 h-4" /></button>
                  <span className="text-sm text-slate-500 ml-2">
                    Total : <strong className="text-emerald-700">{(selected.prix_b2c_ttc * quantite).toFixed(2)} €</strong>
                  </span>
                </div>
              </div>

              <div className="border-t pt-3 space-y-2.5">
                <Field label="Nom complet *" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} testid="input-nom" />
                <Field label="Téléphone *" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="input-telephone" type="tel" />
                <Field label="E-mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="input-email" type="email" />
                {/* Adresse séparée en 3 champs */}
                <Field label="Rue et numéro *" value={form.adresse} onChange={(v) => setForm({ ...form, adresse: v })} testid="input-adresse" />
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <label className="text-sm font-medium text-slate-700 block mb-1">Code postal *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.code_postal}
                      onChange={(e) => setForm({ ...form, code_postal: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="input-code-postal"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-700 block mb-1">Ville *</label>
                    <input
                      type="text"
                      value={form.ville}
                      onChange={(e) => setForm({ ...form, ville: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="input-ville"
                    />
                  </div>
                </div>
                <Field label="N° de commande / client (si connu)" value={form.numero_commande} onChange={(v) => setForm({ ...form, numero_commande: v })} testid="input-numero" />
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Commentaire</label>
                  <textarea
                    value={form.commentaire}
                    onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Une remarque, un étage, une préférence horaire…"
                    data-testid="input-commentaire"
                  />
                </div>
              </div>

              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>}

              {/* Rassurance juste au-dessus du bouton */}
              <p className="text-xs text-center text-slate-500 pt-1 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Aucun paiement maintenant — vous serez contacté avant la livraison.
              </p>

              <button
                onClick={submitPrecommande}
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5" /> Réserver mes chaises</>}
              </button>
            </div>
          </div>
        )}

        {/* ---- STEP SUCCESS ---- */}
        {step === "success" && (
          <div className="bg-white rounded-2xl shadow-2xl border p-6 sm:p-8" data-testid="success-block">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-1">Votre précommande est enregistrée !</h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Merci, nous avons bien reçu votre demande de réservation.
              </p>
            </div>

            <div className="mt-5 border-t pt-5">
              <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <MousePointerClick className="w-4 h-4" /> Et maintenant ?
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                Dès que les chaises sélectionnées seront disponibles, notre équipe vous contactera directement afin de confirmer votre commande et d&apos;organiser la livraison à domicile.
              </p>
            </div>

            <div className="mt-4 p-3 sm:p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-sm text-emerald-900 font-semibold flex items-center gap-1.5">
                <HandCoins className="w-4 h-4" /> Aucun paiement n&apos;est demandé maintenant.
              </p>
              <p className="text-xs text-emerald-800 mt-1">
                Le paiement s&apos;effectuera à la livraison et un reçu de paiement vous sera remis.
              </p>
            </div>

            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 italic">
              « Vous n&apos;avez rien d&apos;autre à faire pour le moment. Nous vous contacterons dès que votre commande pourra être préparée. »
            </div>

            {/* CTA — réserver un créneau de retrait au dépôt-showroom */}
            <a
              href="/prendre-rdv?type=retrait"
              className="mt-4 block bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-2xl shadow-sm p-4 hover:shadow-md transition"
              data-testid="cta-showroom-retrait"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <CalendarClock className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">Préférez venir retirer votre commande ?</p>
                  <p className="text-xs text-emerald-100 leading-snug">Réservez un créneau au dépôt-showroom d&apos;Anderlecht — préparation à l&apos;avance</p>
                </div>
                <ChevronLeft className="w-5 h-5 rotate-180" />
              </div>
            </a>

            <div className="mt-5 text-center border-t pt-4">
              <p className="text-xs text-slate-500">Une question ?</p>
              <a href={`tel:${NUMERO_TOPCOCOON}`} className="inline-flex items-center gap-1.5 mt-1 font-bold text-indigo-700 text-lg" data-testid="success-phone">
                <Phone className="w-4 h-4" /> {NUMERO_TOPCOCOON}
              </a>
              <p className="text-xs text-slate-400 mt-3">L&apos;équipe TopCocoon</p>
            </div>
          </div>
        )}

        {step === "list" && (
          <div className="text-center text-xs text-slate-400 mt-6">
            Une question ? <a href={`tel:${NUMERO_TOPCOCOON}`} className="text-indigo-600 underline">{NUMERO_TOPCOCOON}</a>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        data-testid={testid}
      />
    </div>
  );
}
